// The theme engine. A theme is a set of values for the same CSS variables (src/theme/tokens.css).
// Switching one is a change of two attributes on <html>; nothing else moves.
import { useEffect } from "react";
import { settings, useSettings, type Settings } from "@/app/settings-store";

export type ThemeId = Settings["theme"];

export const THEMES: { id: ThemeId; name: string; forWhom: string; available: boolean }[] = [
  { id: "plain", name: "Plain", forWhom: "Calm and clear. The default.", available: true },
  { id: "backstage", name: "Backstage", forWhom: "Near-black and amber, huge buttons. Gloves on.", available: true },
  { id: "dialup", name: "Dial-Up", forWhom: "Bevelled panels and a command line. Keyboard first.", available: true },
  { id: "arcade", name: "Arcade", forWhom: "Neon on purple, a scoreboard. Focus mode calms it.", available: true },
];

export type DevicePrefs = { prefersDark: boolean; prefersReducedMotion: boolean };

export function applyTheme(s: Settings, device: DevicePrefs) {
  const root = document.documentElement;
  const theme = THEMES.find((t) => t.id === s.theme && t.available) ? s.theme : "plain";
  root.dataset.theme = theme;
  root.dataset.mode = s.mode === "system" ? (device.prefersDark ? "dark" : "light") : s.mode;
  if (s.motion === "reduce") root.dataset.motion = "reduce";
  else delete root.dataset.motion;
  if (s.focus) root.dataset.focus = "on";
  else delete root.dataset.focus;
}

export function readDevicePrefs(): DevicePrefs {
  return {
    prefersDark: window.matchMedia("(prefers-color-scheme: dark)").matches,
    prefersReducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  };
}

/** True when motion should be kept to a minimum: the device says so, or the person did in Settings. */
export function motionReduced(): boolean {
  return settings.get().motion === "reduce" || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Keeps <html> in step with the settings and the device. Mount once, near the top of the app. */
export function ThemeEffect() {
  const s = useSettings();
  useEffect(() => {
    applyTheme(s, readDevicePrefs());
    const dark = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme(settings.get(), readDevicePrefs());
    dark.addEventListener("change", onChange);
    return () => dark.removeEventListener("change", onChange);
  }, [s]);
  return null;
}
